import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { getCurrentBasicDetail } from 'app/basicInformationSlice/basicInformationSlice';
import partnerServices from 'services/partnerServices';
import authorizationServices from 'services/authorizationServices';
import basicInformationServices from 'services/basicInformationApi';
import { USER_ROLE } from 'constants/common';
import { getUserId } from 'utils/sessionStorage';
import toast from 'react-hot-toast';
import { useChatList } from 'components/layout/ChatListContext';

interface ConnectChildModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ConnectChildModal: React.FC<ConnectChildModalProps> = ({ open, onClose, onSuccess }) => {
  const dispatch = useAppDispatch();
  const { refreshChatList } = useChatList();
  const personalStore = useAppSelector((store) => store.basicInformation.myBasicInformation);
  const studentId = personalStore?.student_id ?? getUserId();

  const [email, setEmail] = useState('');
  const [gradMapId, setGradMapId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [namesLocked, setNamesLocked] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [gradMapIdChecking, setGradMapIdChecking] = useState(false);
  const [gradMapIdError, setGradMapIdError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);
  /** Used so email/grad-map blur can skip lookup when focus moves to Send (blur fires before click). */
  const sendRequestButtonRef = useRef<HTMLButtonElement | null>(null);
  const [error, setError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');

  const handleClose = () => {
    setEmail('');
    setGradMapId('');
    setFirstName('');
    setLastName('');
    setNamesLocked(false);
    setEmailChecking(false);
    setGradMapIdChecking(false);
    setGradMapIdError('');
    setError('');
    setFirstNameError('');
    setLastNameError('');
    onClose();
  };

  const parseGradMapIdInput = (raw: string) => raw.replace(/^GM-?/i, '').trim();

  const getStudentFromGradMapId = async (): Promise<any | null> => {
    const idMatch = parseGradMapIdInput(gradMapId);
    if (!idMatch) return null;
    try {
      return await authorizationServices.getStudent(idMatch);
    } catch {
      return null;
    }
  };

  const isFocusMovingToSendButton = (e: React.FocusEvent) => {
    const next = e.relatedTarget as Node | null;
    return Boolean(next && sendRequestButtonRef.current?.contains(next));
  };

  const handleEmailBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    if (isFocusMovingToSendButton(e)) {
      return;
    }
    const raw = email?.trim() ?? '';
    if (!raw) {
      setNamesLocked(false);
      setFirstName('');
      setLastName('');
      return;
    }
    const basicEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!basicEmail.test(raw)) {
      setNamesLocked(false);
      return;
    }
    try {
      setEmailChecking(true);
      const studentObj: any = await partnerServices.getStudentByEmail(raw);
      if (studentObj && studentObj.user_role === USER_ROLE.student) {
        setFirstName(studentObj.first_name ?? '');
        setLastName(studentObj.last_name ?? '');
        setNamesLocked(true);
      } else {
        setNamesLocked(false);
        // Keep any typed names — email may be new or lookup empty; invite still uses first/last.
      }
    } catch {
      setNamesLocked(false);
    } finally {
      setEmailChecking(false);
    }
  };

  const handleGradMapIdBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    if (isFocusMovingToSendButton(e)) {
      return;
    }
    const idMatch = parseGradMapIdInput(gradMapId);
    if (!idMatch) {
      setGradMapIdError('');
      return;
    }
    try {
      setGradMapIdChecking(true);
      setGradMapIdError('');
      setError('');
      const data: any = await basicInformationServices.getBasicDetail(idMatch);
      if (!data) {
        setNamesLocked(false);
        setGradMapIdError('GradMap ID not found. Please check and try again.');
        return;
      }
      const em = (data?.email ?? '').trim();
      const fn = (data?.first_name ?? '').trim();
      const ln = (data?.last_name ?? '').trim();
      if (em && fn && ln) {
        setEmail(em);
        setFirstName(fn);
        setLastName(ln);
        setNamesLocked(true);
        setGradMapIdError('');
      } else {
        setNamesLocked(false);
        setGradMapIdError(
          'No profile found for this GradMap ID or profile is missing email or name.'
        );
      }
    } catch {
      setNamesLocked(false);
      setGradMapIdError('GradMap ID not found. Please check and try again.');
    } finally {
      setGradMapIdChecking(false);
    }
  };

  const handleSubmit = async () => {
    if (submitInFlightRef.current) return;

    setError('');
    setFirstNameError('');
    setLastNameError('');
    if (gradMapIdError) {
      return;
    }

    submitInFlightRef.current = true;
    setIsSubmitting(true);
    try {
      let studentEmailToUse = email?.trim() || '';
      let studentObj: any = null;

      if (studentEmailToUse) {
        studentObj = await partnerServices.getStudentByEmail(studentEmailToUse);
      } else if (gradMapId?.trim()) {
        studentObj = await getStudentFromGradMapId();
        studentEmailToUse = studentObj?.email?.trim() || '';
      }

      if (!studentEmailToUse) {
        setError('Please enter your child\'s email address or GradMap ID.');
        return;
      }

      if (!studentId) {
        setError('Session expired. Please log in again.');
        return;
      }

      if (studentObj && studentObj.user_role && studentObj.user_role !== USER_ROLE.student) {
        setError('The email address or GradMap ID you entered is not a student account.');
        return;
      }

      const fn =
        (firstName?.trim() || studentObj?.first_name?.trim() || '') ?? '';
      const ln = (lastName?.trim() || studentObj?.last_name?.trim() || '') ?? '';

      const isExistingStudent = studentObj && studentObj.user_role === USER_ROLE.student;
      if (!isExistingStudent && (!fn || !ln)) {
        setFirstNameError(!fn ? 'Required' : '');
        setLastNameError(!ln ? 'Required' : '');
        setError('Please enter your child\'s first and last name to send the invitation.');
        return;
      }

      await dispatch(getCurrentBasicDetail(studentId)).unwrap();

      if (isExistingStudent) {
        await partnerServices.parent_InviteStudent(studentId, studentEmailToUse);
      } else {
        await partnerServices.parent_InviteStudent(studentId, studentEmailToUse, fn, ln);
      }

      toast.success('Your student has been invited.');
      refreshChatList();
      handleClose();
      onSuccess?.();
    } catch (err: any) {
      const serverData = err?.response?.data;
      setError(serverData?.message || err?.message || 'An error occurred. Please try again.');
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: 20,
          fontWeight: 700,
          color: '#212529',
          position: 'relative',
          pr: 5,
        }}
      >
        Connect to Your Child's GradMap
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#6c757d',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, fontSize: 14, color: '#6c757d', lineHeight: 1.5 }}>
          Enter your child's email address or their GradMap ID to link your accounts. Your child will
          receive a notification to confirm.
        </Box>

        <Box sx={{ mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 140px', minWidth: 0 }}>
            <Box
              component="label"
              sx={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: '#212529',
                mb: 0.75,
              }}
            >
              Child&apos;s first name
              {!namesLocked && <span style={{ color: '#9ca3af', fontWeight: 400 }}> (if new)</span>}
            </Box>
            <TextField
              fullWidth
              placeholder="First name"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                setFirstNameError('');
                setError('');
              }}
              disabled={namesLocked}
              error={Boolean(firstNameError)}
              helperText={firstNameError || undefined}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                },
                '& .MuiOutlinedInput-input': {
                  py: 1.25,
                  px: 1.5,
                },
              }}
            />
          </Box>
          <Box sx={{ flex: '1 1 140px', minWidth: 0 }}>
            <Box
              component="label"
              sx={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: '#212529',
                mb: 0.75,
              }}
            >
              Child&apos;s last name
              {!namesLocked && <span style={{ color: '#9ca3af', fontWeight: 400 }}> (if new)</span>}
            </Box>
            <TextField
              fullWidth
              placeholder="Last name"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                setLastNameError('');
                setError('');
              }}
              disabled={namesLocked}
              error={Boolean(lastNameError)}
              helperText={lastNameError || undefined}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                },
                '& .MuiOutlinedInput-input': {
                  py: 1.25,
                  px: 1.5,
                },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box
            component="label"
            sx={{
              display: 'block',
              fontSize: 14,
              fontWeight: 500,
              color: '#212529',
              mb: 0.75,
            }}
          >
            Child's Email Address
          </Box>
          <TextField
            fullWidth
            placeholder="name@example.com"
            value={email}
            onChange={(e) => {
              const v = e.target.value;
              setEmail(v);
              setGradMapIdError('');
              if (namesLocked) {
                setNamesLocked(false);
                setFirstName('');
                setLastName('');
              }
              setError('');
            }}
            onBlur={handleEmailBlur}
            disabled={emailChecking}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#fff',
                alignItems: 'center',
              },
              '& .MuiOutlinedInput-input': {
                py: 1.25,
                px: 1.5,
              },
            }}
          />
          {emailChecking && (
            <Box sx={{ fontSize: 12, color: '#6c757d', mt: 0.5 }}>Looking up account…</Box>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box
            component="label"
            sx={{
              display: 'block',
              fontSize: 14,
              fontWeight: 500,
              color: '#212529',
              mb: 0.75,
            }}
          >
            OR Child's GradMap ID <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
          </Box>
          <TextField
            fullWidth
            placeholder="e.g. GM-123456"
            value={gradMapId}
            onChange={(e) => {
              setGradMapId(e.target.value);
              setGradMapIdError('');
              setError('');
            }}
            onBlur={handleGradMapIdBlur}
            disabled={gradMapIdChecking}
            error={Boolean(gradMapIdError)}
            helperText={gradMapIdError || (gradMapIdChecking ? 'Looking up GradMap ID…' : undefined)}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#fff',
                alignItems: 'center',
              },
              '& .MuiOutlinedInput-input': {
                py: 1.25,
                px: 1.5,
              },
            }}
          />
        </Box>

        {error && (
          <Box sx={{ color: '#dc2626', fontSize: 13, mb: 2 }}>{error}</Box>
        )}
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', p: 3, pt: 0 }}>
        <Button
          ref={sendRequestButtonRef}
          variant="contained"
          onClick={handleSubmit}
          disabled={
            isSubmitting ||
            Boolean(gradMapIdError) ||
            (!email?.trim() && !gradMapId?.trim())
          }
          sx={{
            backgroundColor: '#4d88f0',
            color: 'white',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 15,
            py: 1.5,
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: '#3d78e0',
            },
          }}
        >
          {isSubmitting
            ? 'Sending...'
            : emailChecking || gradMapIdChecking
              ? 'Loading...'
              : 'Send Connection Request'}
        </Button>
        <Button
          onClick={handleClose}
          sx={{
            color: '#6c757d',
            textTransform: 'none',
            fontSize: 14,
            mt: 1,
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectChildModal;
